import type {WebCommitInfo} from '@github-ui/code-view-types'
import {Button, Dialog, Flash, FormControl, Spinner, Textarea} from '@primer/react'
import {Suspense, lazy, useCallback, useRef, useState} from 'react'

import styles from './TriageAgentConfigModal.module.css'
import type {CanCommitStatus} from './use-triage-agent-config'
import {useTriageAgentConfig, useSaveTriageAgentConfig} from './use-triage-agent-config'

import type {WebCommitDialogState} from '@github-ui/web-commit-dialog'

// Lazy load WebCommitDialog for SSR compatibility
const WebCommitDialog = lazy(async () => {
  const module = await import('@github-ui/web-commit-dialog')
  return {default: module.WebCommitDialog}
})

type TriageAgentConfigModalProps = {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  repoOwner: string
  repoName: string
}

function buildWebCommitInfo(
  canCommitStatus: CanCommitStatus,
  authorEmails: string[],
  defaultEmail: string,
  defaultNewBranchName: string,
  commitOid: string,
  dcoSignoffEnabled: boolean,
): WebCommitInfo {
  return {
    canCommitStatus,
    authorEmails,
    defaultEmail,
    defaultNewBranchName,
    commitOid,
    dcoSignoffEnabled,
    forkedRepo: undefined,
    lockedOnMigration: false,
    pr: '',
    repoHeadEmpty: false,
    saveUrl: '',
    shouldFork: false,
    shouldUpdate: false,
    suggestionsUrlEmoji: '',
    suggestionsUrlIssue: '',
    suggestionsUrlMention: '',
  }
}

export function TriageAgentConfigModal({isOpen, onClose, onSaved, repoOwner, repoName}: TriageAgentConfigModalProps) {
  const {data: configData, isLoading, isError} = useTriageAgentConfig(repoOwner, repoName, isOpen)
  const {mutateAsync: saveConfig} = useSaveTriageAgentConfig(repoOwner, repoName)

  // Content: null means user hasn't edited yet, use server data
  const [contentOverride, setContentOverride] = useState<string | null>(null)
  const [phase, setPhase] = useState<'edit' | 'commit'>('edit')

  // Commit dialog state
  const [dialogState, setDialogState] = useState<WebCommitDialogState>('closed')
  const [commitMessage, setCommitMessage] = useState('')
  const [commitDescription, setCommitDescription] = useState('')
  const [isQuickPull, setIsQuickPull] = useState(false)
  const [authorEmail, setAuthorEmail] = useState<string | undefined>(undefined)
  const [prTargetBranch, setPRTargetBranch] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const returnFocusRef = useRef<HTMLButtonElement>(null)

  // Derive content and commit defaults from server data
  const content = contentOverride ?? configData?.content ?? ''
  const serverRequiresQuickPull = configData?.commit_info.can_commit_status !== 'allowed'
  const effectiveIsQuickPull = serverRequiresQuickPull || isQuickPull
  const effectivePRTargetBranch = prTargetBranch || configData?.commit_info.default_new_branch_name || ''

  const resetState = useCallback(() => {
    setContentOverride(null)
    setPhase('edit')
    setDialogState('closed')
    setCommitMessage('')
    setCommitDescription('')
    setIsQuickPull(false)
    setAuthorEmail(undefined)
    setPRTargetBranch('')
    setErrorMessage('')
  }, [])

  const handleClose = useCallback(() => {
    resetState()
    onClose()
  }, [resetState, onClose])

  const handleSaveChangesClick = useCallback(() => {
    setPhase('commit')
    setDialogState('pending')
  }, [])

  const handleCommitDialogStateChange = useCallback(
    (state: WebCommitDialogState) => {
      if (state === 'closed') {
        // User cancelled the commit dialog, go back to editing
        setPhase('edit')
        setDialogState('closed')
        setErrorMessage('')
        return
      }
      setDialogState(state)
    },
    [setPhase, setDialogState, setErrorMessage],
  )

  const handleSave = useCallback(async () => {
    setDialogState('saving')
    setErrorMessage('')

    try {
      const result = await saveConfig({
        content,
        message: commitMessage,
        description: commitDescription,
        commit_choice: effectiveIsQuickPull ? 'quick-pull' : 'direct',
        target_branch: effectiveIsQuickPull ? effectivePRTargetBranch : undefined,
        author_email: authorEmail,
        blob_oid: configData?.blob_oid,
      })

      setDialogState('saved')
      resetState()
      onSaved()

      // Navigate to the PR or redirect URL if one was returned
      const redirectUrl = result?.pull_request_url || result?.redirect_url
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
    } catch (error) {
      setDialogState('pending')
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save configuration')
    }
  }, [
    content,
    commitMessage,
    commitDescription,
    effectiveIsQuickPull,
    effectivePRTargetBranch,
    authorEmail,
    configData,
    saveConfig,
    resetState,
    onSaved,
  ])

  if (!isOpen) return null

  // Commit phase: show the WebCommitDialog
  if (phase === 'commit' && configData) {
    const {commit_info: commitInfo} = configData
    const webCommitInfo = buildWebCommitInfo(
      commitInfo.can_commit_status,
      commitInfo.author_emails,
      commitInfo.default_email,
      commitInfo.default_new_branch_name,
      commitInfo.commit_oid,
      commitInfo.dco_signoff_enabled,
    )

    const placeholderMessage = configData.exists ? 'Update triage agent configuration' : 'Configure triage agent'

    const fileStatuses: Record<string, 'A' | 'M'> = {
      [configData.config_path]: configData.exists ? 'M' : 'A',
    }

    return (
      <Suspense fallback={null}>
        <WebCommitDialog
          message={commitMessage}
          setMessage={setCommitMessage}
          description={commitDescription}
          setDescription={setCommitDescription}
          isQuickPull={effectiveIsQuickPull}
          setIsQuickPull={setIsQuickPull}
          prTargetBranch={effectivePRTargetBranch}
          setPRTargetBranch={setPRTargetBranch}
          errorMessage={errorMessage}
          dialogState={dialogState}
          setDialogState={handleCommitDialogStateChange}
          setAuthorEmail={setAuthorEmail}
          onSave={handleSave}
          refName={configData.default_branch}
          webCommitInfo={webCommitInfo}
          helpUrl="https://docs.github.com/articles/about-protected-branches"
          placeholderMessage={placeholderMessage}
          fileStatuses={fileStatuses}
          returnFocusRef={returnFocusRef}
        />
      </Suspense>
    )
  }

  // Edit phase: show the config editor
  return (
    <Dialog title="Configure Triage Agent" width="xlarge" onClose={handleClose}>
      <div className={styles.dialogBody} data-testid="config-modal-body">
        {isLoading && (
          <div className={styles.spinnerContainer}>
            <Spinner aria-label="Loading configuration" />
          </div>
        )}

        {isError && <Flash variant="danger">Failed to load triage agent configuration.</Flash>}

        {!isLoading && !isError && (
          <>
            <p className={styles.description}>
              Edit the triage agent configuration file
              <code className={styles.configPath}>{configData?.config_path}</code>
            </p>

            <FormControl>
              <FormControl.Label visuallyHidden>Configuration content</FormControl.Label>
              <Textarea
                className={styles.configTextarea}
                value={content}
                onChange={e => setContentOverride(e.target.value)}
                aria-label="Triage agent configuration"
                data-testid="config-textarea"
                block
              />
            </FormControl>
          </>
        )}

        <div className={styles.actionButtons}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            ref={returnFocusRef}
            variant="primary"
            onClick={handleSaveChangesClick}
            disabled={isLoading || isError || !content.trim()}
            data-testid="save-changes-button"
          >
            Save changes...
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
